import {
  type Filters,
  type OasDocument3XExtended,
  isNonNullable,
} from '@ipfabric/utils';
import type { DeviceViewNodeEntity } from 'components/DeviceView/deviceViewTypes';
import { pipe } from 'fp-ts/lib/function';
import { joinFilters } from '../../joinFilters';
import { getCloudConstructFilters } from './getCloudConstructFilters';
import {
  getNodeCustomFilters,
  hostsApiEndpoint,
  isApiFilterFactory,
} from './getNodeCustomFilters';

const hostsArrayAttributes = ['accessPoints', 'edges', 'gateways'];

export const getNodeFilters = (
  deviceViewEntity: DeviceViewNodeEntity,
  apiEndpoint: string,
  oas: OasDocument3XExtended,
): Filters | undefined => {
  // Cloud constructs (NIM-24243): each (constructKind, apiEndpoint) pair has
  // a bespoke hidden filter from the design — not the on-prem `sn`-eq filter.
  const cloudFilter = getCloudConstructFilters(deviceViewEntity, apiEndpoint);
  if (cloudFilter !== null) {
    return cloudFilter;
  }

  const customFilters = getNodeCustomFilters(
    deviceViewEntity,
    apiEndpoint,
    oas,
  );
  if (isApiFilterFactory(customFilters)) {
    return customFilters(deviceViewEntity);
  }
  if (!customFilters.length) {
    return undefined;
  }
  return pipe(
    customFilters.map((attribute: string): Filters => {
      if (
        (deviceViewEntity.nodeType === 'phoneGroup' ||
          deviceViewEntity.nodeType === 'hostGroup') &&
        isNonNullable(deviceViewEntity.parentNodeSn)
      ) {
        return {
          [attribute]: ['any', 'sn', 'eq', deviceViewEntity.parentNodeSn],
        };
      }
      if (
        deviceViewEntity.nodeType === 'host' &&
        deviceViewEntity.extra?.ip !== undefined
      ) {
        return {
          ip: ['eq', deviceViewEntity.extra.ip],
        };
      }
      if (
        apiEndpoint === hostsApiEndpoint &&
        hostsArrayAttributes.includes(attribute)
      ) {
        return {
          [attribute]: ['any', 'sn', 'eq', deviceViewEntity.sn],
        };
      }
      return {
        [attribute]: ['eq', deviceViewEntity.sn],
      };
    }),
    (filters) => joinFilters('or', filters),
    (filters) => {
      if (
        deviceViewEntity.nodeType === 'phoneGroup' ||
        deviceViewEntity.nodeType === 'phone'
      ) {
        return {
          and: [{ type: ['any', 'eq', 'phone'] }, filters],
        };
      }
      if (deviceViewEntity.nodeType === 'hostGroup') {
        return {
          and: [{ type: ['any', 'eq', 'user'] }, filters],
        };
      }
      return filters;
    },
  );
};
